"use client"

import { useState, useEffect, useMemo, useRef, Suspense } from "react"
import { usePrerenderSafeSearchParams } from "@/hooks/use-prerender-safe-search-params"
import { SearchParamsHydrationGate } from "@/components/ssr/search-params-hydration-gate"
import { ArrowUp } from "lucide-react"
import {
  HeadlineHero,
  LawnPrefillBanner,
  RelatedTools,
  ResultEmailCaptureCard,
  ToolPageLoadingFallback,
} from "@/components/tools/shared"
import { BreadcrumbComponent } from "@/components/ui/breadcrumb"
import { CuratedProductPicks } from "@/components/tools/shared/curated-product-picks"
import { NewsletterSignupModal } from "@/components/ui/newsletter-signup-modal"
import {
  LimeCalculatorForm,
  DEFAULT_LIME_FORM,
  type LimeFormState,
} from "@/components/tools/lime-calculator-form"
import { LimeResultCard } from "@/components/tools/lime-result-card"
import { calculateLime } from "@/lib/lime-calculator/calculator"
import { getLimePicks } from "@/lib/lime-calculator/product-picks"
import { GRASS_AGRONOMY } from "@/lib/soil-test/agronomy-tables"
import { GRASS_KEYS, type GrassKey } from "@/lib/soil-test/types"
import { useDefaultLawn } from "@/hooks/use-default-lawn"
import { useNewsletterPrompt } from "@/hooks/useNewsletterPrompt"
import { useFromTool } from "@/hooks/use-from-tool"
import { analytics, stashEmailCaptureAttribution } from "@/lib/analytics"
import { buildToolUrl } from "@/lib/result-tool-urls"
import { motion } from "motion/react"
import { FADE_UP, FADE_TRANSITION } from "@/lib/motion-presets"
import { Reveal } from "@/components/motion"

const GRASS_ALIASES: Record<string, GrassKey> = {
  kbg: "kentucky-bluegrass",
  bluegrass: "kentucky-bluegrass",
  fescue: "tall-fescue",
  tallfescue: "tall-fescue",
  finefescue: "fine-fescue",
  rye: "perennial-ryegrass",
  ryegrass: "perennial-ryegrass",
  perennialrye: "perennial-ryegrass",
  bermuda: "bermudagrass",
  zoysia: "zoysiagrass",
  staug: "st-augustinegrass",
  staugustine: "st-augustinegrass",
  bahia: "bahiagrass",
  buffalo: "buffalograss",
}

/** Map a URL/saved-lawn grass string to a canonical engine GrassKey, or null. */
function resolveGrassKey(input?: string | null): GrassKey | null {
  if (!input) return null
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z]+/g, "")
  const target = norm(input)
  for (const key of GRASS_KEYS) {
    if (norm(key) === target) return key
    if (norm(GRASS_AGRONOMY[key].displayName) === target) return key
  }
  return GRASS_ALIASES[target] ?? null
}

function toInput(v: LimeFormState) {
  return {
    currentPh: v.currentPh,
    grassType: v.grassType,
    soilTexture: v.soilTexture,
    lawnSizeSqft: v.lawnSizeSqft || 0,
    magnesium: v.magnesium,
    zipCode: v.zipCode || undefined,
  }
}

function LimeCalculatorContent() {
  const searchParams = usePrerenderSafeSearchParams()
  useFromTool("lime-calculator")
  const {
    lawns,
    selectedLawnId,
    setSelectedLawnId,
    prefill: lawnPrefill,
    isLoading: isLoadingLawn,
  } = useDefaultLawn()
  const { trackUsage, showNewsletterModal, closeNewsletterModal, usageCount } = useNewsletterPrompt()

  // Lazy-init from ?grass=/?size=/?zip= deep links (no derived-state effect).
  const [value, setValue] = useState<LimeFormState>(() => {
    const grass = resolveGrassKey(searchParams.get("grass"))
    const size = Number(searchParams.get("size"))
    const zip = searchParams.get("zip")
    const zipOk = !!zip && /^\d{5}$/.test(zip)
    return {
      ...DEFAULT_LIME_FORM,
      grassType: grass ?? DEFAULT_LIME_FORM.grassType,
      lawnSizeSqft: size && !Number.isNaN(size) ? size : DEFAULT_LIME_FORM.lawnSizeSqft,
      zipCode: zipOk ? (zip as string) : "",
      advancedOpen: zipOk,
    }
  })
  const [interacted, setInteracted] = useState(false)
  const railRef = useRef<HTMLDivElement>(null)

  // Saved-lawn prefill: async external data, applied only before the user touches
  // anything and only when there were no URL params to honor.
  useEffect(() => {
    if (interacted || isLoadingLawn) return
    const hasUrl =
      searchParams.get("grass") || searchParams.get("size") || searchParams.get("zip")
    if (hasUrl) return
    setValue((v) => {
      const next = { ...v }
      const gk = resolveGrassKey(lawnPrefill.grassType)
      if (gk) next.grassType = gk
      if (lawnPrefill.lawnSizeSqft) next.lawnSizeSqft = lawnPrefill.lawnSizeSqft
      if (lawnPrefill.zipCode && /^\d{5}$/.test(lawnPrefill.zipCode)) {
        next.zipCode = lawnPrefill.zipCode
        next.advancedOpen = true
      }
      return next
    })
  }, [lawnPrefill, isLoadingLawn, interacted, searchParams])

  const result = useMemo(() => calculateLime(toInput(value)), [value])

  const handleChange = (next: LimeFormState) => {
    setValue(next)
    if (!interacted) {
      setInteracted(true)
      trackUsage("lime-calculator")
      const r = calculateLime(toInput(next))
      analytics.calculatorUsed("lime", {
        inputs: {
          current_ph: next.currentPh,
          grass_type: next.grassType,
          soil_texture: next.soilTexture,
          lawn_size_sqft: next.lawnSizeSqft,
          magnesium: next.magnesium,
        },
        result: {
          direction: r.direction,
          lbs_per_1000: r.lbsPer1000 ?? undefined,
          total_lbs: r.totalLbs ?? undefined,
          applications: r.applications ?? undefined,
        },
        source: "live_update",
      })
    }
  }

  const picks = getLimePicks(result.direction)
  const grassDisplay = GRASS_AGRONOMY[value.grassType].displayName
  const dockAmount = result.inRange
    ? `pH ${result.currentPh.toFixed(1)}: already in range`
    : `${(result.totalLbs ?? 0).toLocaleString()} lbs ${result.amendment?.toLowerCase()}`
  const dockSub = result.inRange
    ? "No lime needed"
    : `${result.applications} ${result.applications === 1 ? "application" : "applications"} · pH ${result.currentPh.toFixed(1)} → ${result.targetPh.toFixed(1)}`

  return (
    <div className="min-h-screen bg-linear-to-br from-green-50 via-white to-gray-50">
      <Reveal onMount y={6} className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8 pt-4 md:pt-8 pb-24 md:pb-16">
        <BreadcrumbComponent
          items={[
            { label: "Home", href: "/" },
            { label: "Tools", href: "/tools" },
            { label: "Lawn Lime Calculator" },
          ]}
        />

        <div className="mb-6 md:mb-8">
          <HeadlineHero
            eyebrow="Raises or lowers pH"
            title="How much lime per"
            accent="1,000 sq ft?"
            subtitle="Enter your soil pH from a test strip, your grass type, and your soil. You'll get pounds of lime (or sulfur, if your pH is high) per 1,000 sq ft, the total for your lawn, and how to split it safely."
          />
        </div>

        <LawnPrefillBanner
          lawns={lawns}
          selectedLawnId={selectedLawnId}
          onSelect={(id) => {
            setSelectedLawnId(id)
            setInteracted(false)
          }}
        />

        {/* Two-column: form (left) + sticky results rail (right) */}
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-7 items-start mt-2">
          <LimeCalculatorForm value={value} onChange={handleChange} regionHint={result.regionHint} />

          <div ref={railRef} className="flex flex-col gap-4 lg:sticky lg:top-24">
            <LimeResultCard result={result} />

            {picks.length > 0 && (
              <CuratedProductPicks
                picks={picks}
                title="Matched to your result"
                description={`For your ${grassDisplay} lawn`}
                trackingSlug="tool:lime-calculator"
              />
            )}

            <ResultEmailCaptureCard
              source="tool_result_lime"
              toolDisplayName="lime plan"
              subcopy="Your amounts, timing, and a re-test reminder, sent to your inbox."
              grassType={grassDisplay}
              zipCode={value.zipCode || undefined}
              accountLead={{
                redirectTo: buildToolUrl("lime-calculator", {
                  grassType: grassDisplay,
                  zip: value.zipCode || undefined,
                }),
                headline: `Save my lime plan for ${grassDisplay}`,
                subcopy: "Keep this lime plan in a free account and track your lawn over time.",
                // Grass-aware (grassType + zipCode passed) so the subscribe route
                // sends the 12-month calendar. Name it, per .claude/rules/tools-pages.md.
                dividerLabel: "or get your 12-month calendar by email",
                onBeforeOAuth: () => stashEmailCaptureAttribution("tool_result_lime"),
              }}
              onCaptured={closeNewsletterModal}
            />
          </div>
        </div>

        <div className="mt-12">
          <RelatedTools
            currentToolId="lime-calculator"
            prefillValues={{
              zip: value.zipCode || undefined,
              grassType: grassDisplay,
              lawnSize: value.lawnSizeSqft || undefined,
            }}
          />
        </div>
      </Reveal>

      {/* Mobile bottom dock — the rail is below the form on small screens */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-50 flex items-center gap-3.5 px-4 py-3 bg-white/95 backdrop-blur border-t border-gray-200 shadow-[0_-8px_30px_rgba(0,0,0,0.08)]">
        <div className="flex-1 min-w-0">
          <div className="text-[15px] font-extrabold text-gray-900 truncate">{dockAmount}</div>
          <div className="text-xs font-semibold text-green-700">{dockSub}</div>
        </div>
        <button
          type="button"
          onClick={() => railRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
          className="flex items-center gap-1.5 min-h-[44px] px-4.5 rounded-full text-sm font-bold text-white bg-linear-to-r from-green-600 to-emerald-600 whitespace-nowrap shadow-[0_8px_20px_-6px_rgba(22,163,74,0.3)]"
        >
          See my plan
          <ArrowUp className="w-[15px] h-[15px]" />
        </button>
      </div>

      <NewsletterSignupModal
        isOpen={showNewsletterModal}
        onClose={closeNewsletterModal}
        toolName="Lawn Lime Calculator"
        usageCount={usageCount}
        grassType={grassDisplay}
        zipCode={value.zipCode || undefined}
      />
    </div>
  )
}

export function LimeCalculatorPageContent() {
  return (
    <Suspense fallback={<ToolPageLoadingFallback toolId="lime-calculator" />}>
      <SearchParamsHydrationGate>
        <LimeCalculatorContent />
      </SearchParamsHydrationGate>
    </Suspense>
  )
}
